import { Injectable } from '@angular/core';
import { ApiService } from '@services/api/api.service';
import { CalendarAPIService } from '@services/api/calendarAPI.service';
import { catchError, map, Observable, of, switchMap, timeout } from 'rxjs';
import { extractMeetLink } from '../utils/chat-call-link.util';

/** Why a call could not be started. */
export type ChatMeetFailure =
  /** No Google account connected, so there is nothing that can mint a room. Recoverable by the user. */
  | 'not-connected'
  /**
   * The event was created and returned, and Google attached no Meet room to it.
   *
   * Worth its own value rather than folding into `failed`, because it means something specific and
   * unretryable. The server asks for the conference correctly, and when Google reports the room as
   * `pending` it re-reads until it resolves — so a room that is merely slow never reaches here.
   * What does reach here is a conference Google marked `failure` or dropped, which is the account:
   * Meet disabled on the Workspace tenant, or a token issued before the Calendar scope was
   * granted. The server logs which. See `docs/backend/chat/chat-video-calls-backend.md` §1.
   */
  | 'no-conference'
  /** Anything else: API down, token revoked, the event could not be found again. */
  | 'failed';

/**
 * The outcome of asking for a meeting room.
 *
 * Carries a failure rather than only a URL, because "we could not make you a room" is a real
 * answer the caller has to handle — and the one thing this must never do is quietly hand back a
 * link that isn't a room. See the note on {@link ChatMeetService} for why that matters here.
 *
 * A flat shape rather than a discriminated union: this project builds with `strictNullChecks`
 * off, where a `true | false` discriminant does not narrow, so a union would push casts onto
 * every caller for no benefit. `url` being null *is* the discriminant.
 */
export interface ChatMeetResult {
  /** The room to share, or null when none could be minted. */
  url: string | null;
  /** Why there is no room. Null when there is one. */
  failure: ChatMeetFailure | null;
  /** Google's consent URL, present when connecting an account is what would fix this. */
  connectUrl?: string | null;
}

/**
 * How long the Calendar round trip gets before it counts as a failure.
 *
 * Generous because the server may spend ~1.2s of it re-reading a room Google is still minting —
 * Meet rooms are created asynchronously, so the first response can legitimately come back
 * `pending` with no link on it yet. Only that case waits; a refused conference answers at once.
 */
const CALENDAR_TIMEOUT_MS = 8000;

/** An instant call is a half-hour slot from now — long enough to be useful, short enough to ignore. */
const MEETING_MINUTES = 30;

/**
 * Starting a Google Meet from a conversation.
 *
 * One path, and it either produces a **real room** — `meet.google.com/mht-xgfn-xwd`, the same link
 * for everybody — or it fails and says why.
 *
 * There used to be a second path that fell back to `meet.google.com/new` whenever the first one
 * could not deliver. That was a mistake worth spelling out, because it looked like resilience and
 * behaved like a bug: `/new` is not a room, it is an instruction to *create* one. Posting it into
 * a thread sent every member off to start their own separate meeting, each alone in it, all
 * convinced they were joining the same call. A call button that fails honestly is far better than
 * one that always appears to work and never connects anyone.
 *
 * Only the person *starting* the call needs Google connected — a room, once minted, is joinable by
 * anyone with the link, account or no account.
 *
 * The insert now answers with the created event, so the link comes straight back. An earlier
 * version had to fetch the whole calendar afterwards and find the event again by a token it had
 * hidden in the description; that is gone, along with the token and the extra round trip.
 */
@Injectable({ providedIn: 'root' })
export class ChatMeetService {
  constructor(
    private readonly calendar: CalendarAPIService,
    private readonly api: ApiService
  ) {}

  /**
   * Mint a room to share. Never throws — every failure arrives as `{ ok: false }` with a reason.
   *
   * @param title Conversation name, used as the calendar event's summary.
   */
  createMeeting(title: string): Observable<ChatMeetResult> {
    return this.calendar.checkGoogleCalendarConnectedStatus().pipe(
      timeout(CALENDAR_TIMEOUT_MS),
      switchMap((response: any) => (response?.connected ? this.insertConferenceEvent(title) : of(this.notConnected(response)))),
      catchError(() => of<ChatMeetResult>({ url: null, failure: 'failed' }))
    );
  }

  /**
   * Ask Google Calendar for an event with a Meet conference attached, and read back its link.
   *
   * The payload is the one the property and project appointment screens already use, down to the
   * shape of `createRequest` — those screens demonstrably get rooms out of this endpoint, so
   * matching them exactly is the difference between a link and a shrug.
   */
  private insertConferenceEvent(title: string): Observable<ChatMeetResult> {
    const start = new Date();
    const end = new Date(start.getTime() + MEETING_MINUTES * 60 * 1000);

    const event = {
      summary: `Remmi Chat: ${title}`.slice(0, 200),
      description: `Video call started from the Remmi chat${title ? ` in "${title}"` : ''}.`,
      start: { dateTime: this.localIso(start), timeZone: this.timeZone },
      end: { dateTime: this.localIso(end), timeZone: this.timeZone },
      visibility: 'private',
      // `requestId` only has to be unique per create; Google returns the room it minted against it.
      conferenceData: {
        createRequest: {
          conferenceSolutionKey: { type: 'hangoutsMeet' },
          requestId: `remmi-chat-${Date.now()}`,
        },
      },
    };

    return this.calendar
      .insertCalendarEvent({
        // An **array**, not a string. The endpoint calls this list `agents` and reduces over it —
        // passing the bare id gets you `TypeError: agents.reduce is not a function` and a 500,
        // which is what a chat call did until this was corrected. The appointment screens pass
        // `form_data.agents`, which is always a list, which is why they never hit it.
        user_id: [this.api.getUserId()],
        // 'Meeting' is the activity type the existing screens use to request a conference.
        type: 'Meeting',
        title: `Remmi Chat: ${title}`,
        event,
        edit: false,
        isInspection: false,
      })
      .pipe(
        timeout(CALENDAR_TIMEOUT_MS),
        map((response: any): ChatMeetResult => {
          if (response?.status === false) {
            return { url: null, failure: 'failed' };
          }

          // `data` is the caller's own copy of the event, verbatim from Google. Preferred over the
          // whole body because a multi-attendee response also carries an `events[]` array, and
          // picking a link out of that could hand back somebody else's room.
          const url = this.findMeetLink(response?.data) ?? this.findMeetLink(response);
          if (url) {
            return { url, failure: null };
          }

          // The event was created and returned, and Google attached no room to it. The server has
          // already waited out an asynchronously-minted conference before answering, so this is
          // the account itself: Meet disabled on the Workspace tenant, or a token issued before
          // the Calendar scope was granted. Neither is fixed by trying again.
          return { url: null, failure: 'no-conference' };
        }),
        catchError(() => of<ChatMeetResult>({ url: null, failure: 'failed' }))
      );
  }

  /**
   * Not connected — and the same response carries the Google consent URL, so the caller can offer
   * the one action that actually fixes this instead of a dead end.
   */
  private notConnected(response: any): ChatMeetResult {
    const connectUrl = typeof response?.data === 'string' ? response.data : null;
    return { url: null, failure: 'not-connected', connectUrl };
  }

  /**
   * Dig a Meet URL out of whatever the insert endpoint answered with.
   *
   * Google puts it on `hangoutLink` and again inside `conferenceData.entryPoints[].uri`, and the
   * backend wraps its responses inconsistently (`data`, `event`, bare). Walking the object is
   * shorter than guessing which of those it is this time, and it keeps working if the wrapper
   * changes. Depth-capped so a response with a cycle or a huge payload can't spin.
   */
  private findMeetLink(payload: unknown, depth = 0): string | null {
    if (!payload || depth > 6) {
      return null;
    }
    if (typeof payload === 'string') {
      return extractMeetLink(payload);
    }
    if (Array.isArray(payload)) {
      for (const entry of payload) {
        const found = this.findMeetLink(entry, depth + 1);
        if (found) {
          return found;
        }
      }
      return null;
    }
    if (typeof payload === 'object') {
      for (const value of Object.values(payload as Record<string, unknown>)) {
        const found = this.findMeetLink(value, depth + 1);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }

  private get timeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * `YYYY-MM-DDTHH:mm:ss` in local time, paired with an explicit `timeZone` above.
   *
   * Google reads the two together, so the string must *not* be UTC — the same shift the
   * appointment screens apply, for the same reason.
   */
  private localIso(date: Date): string {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000).toISOString().split('.')[0];
  }
}
